import { useTranslation } from "@i18n";
import { useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { getFileImage } from "@Services/utils/iconMap";
import type { FrontendAttachment } from "../mailbox.interfaces";

interface AttachmentsModalProps {
  saveAttachments: (files: FrontendAttachment[]) => void;
  onClose: () => void;
}

function AttachmentsModal({ saveAttachments, onClose }: AttachmentsModalProps) {
  const { t } = useTranslation();
  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const [attachments, setAttachments] = useState<FrontendAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = async (files: File[]) => {
    let fileAttachments = [...attachments];
    for (const file of files) {
      fileAttachments.push({
        file,
        attachmentsData: { type: file.type, name: file.name },
      });
    }
    setAttachments(fileAttachments);
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    await handleFiles(files);
    e.target.value = "";
  };

  const handleBrowseFileButtonClick = () => {
    fileUploadRef.current?.click();
  };

  const handleRemoveAttachment = (index: number) => {
    const fileAttachments = [...attachments];
    fileAttachments.splice(index, 1);
    setAttachments(fileAttachments);
  };

  const handleAttachment = async () => {
    saveAttachments(attachments);
    onClose();
  };

  const handleCancelAttachment = () => {
    onClose();
  };

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const attachmentsModalContent = () => (
    <div className="flex flex-col h-full" data-testid="attachmentsModalContent">
      <div
        className={`flex mt-5 p-5 border-2 rounded-md justify-start items-center transition 
          ${isDragging ? "border-blue-500 bg-blue-50" : "border-button_light"}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid="attachmentsModalInput"
      >
        <input
          ref={fileUploadRef}
          type="file"
          multiple
          accept="image/png,image/jpg,image/jpeg,application/pdf"
          onChange={handleChange}
          hidden
        />
        <ButtonComponent onClick={handleBrowseFileButtonClick} cssClass="e-flat" data-testid="browseForAttachments">
          Browse...
        </ButtonComponent>
        <div style={{ fontSize: "0.875rem", paddingInlineStart: "0.625rem" }} data-testid="dropFileInAttachmentsModal">
          {t("Or drop files here")}
        </div>
      </div>
      <div className="flex flex-row my-5 flex-wrap pe-3 overflow-auto gap-4 w-full h-full">
        {attachments.map((data: FrontendAttachment, index: number) => (
          <span key={index} className="flex gap-3 border-solid border-2 rounded-lg items-center">
            <span className="w-14 h-10 flex object-contain" data-testid="attachmentTypeIcon">
              <img src={getFileImage(data.attachmentsData)} />
            </span>
            <span className="w-full" data-testid="attachmentName">
              {data.attachmentsData.name}
            </span>
            <IconComponent
              className="cursor-pointer w-4 h-4 me-2"
              onClick={() => handleRemoveAttachment(index)}
              name="close"
            />
          </span>
        ))}
      </div>
    </div>
  );

  const footerContent = () => (
    <div className="flex gap-4">
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleAttachment}
        disabled={!attachments.length}
        data-testid="attachSelectedBtn"
      >
        {t("Attach Selected")}
      </ButtonComponent>
      <ButtonComponent
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        onClick={handleCancelAttachment}
        data-testid="cancelSelectedBtn"
      >
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <ModalStateDialogComponent
      isVisible
      width="80%"
      headerTitle={t("Add Attachments")}
      onCancel={onClose}
      modelContent={attachmentsModalContent()}
      footerContent={footerContent()}
    />
  );
}

export default AttachmentsModal;
